import type { SyncStatus } from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, post, timeAgo } from "../api";
import { useWorkspace } from "./Shell";
import { useToast } from "./Toast";

/**
 * "Check for new posts now": Brandwall looks on Instagram every 30 minutes by itself. This
 * starts a look right away, for example after adding a hashtag, and says how it went.
 */
export function CheckNow() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const qc = useQueryClient();
  const toast = useToast();
  /** When the person asked; the run counts as done once it finished after this. */
  const [askedAt, setAskedAt] = useState<number | null>(null);
  const status = useQuery({
    queryKey: ["sync", ws],
    queryFn: () => api<SyncStatus>(`/v1/w/${ws}/sync`),
    refetchInterval: (q) => (askedAt || q.state.data?.running ? 3000 : false),
  });
  const s = status.data;

  // When the run finishes, show the new posts and say how many there were.
  useEffect(() => {
    if (!s || !askedAt) return;
    const finished = s.finishedAt ? new Date(s.finishedAt).getTime() : 0;
    // Give up waiting after three minutes; the run still finishes in the background.
    if (Date.now() - askedAt > 180_000) setAskedAt(null);
    else if (!s.running && finished >= askedAt - 1000) {
      setAskedAt(null);
      qc.invalidateQueries({ queryKey: ["posts", ws] });
      qc.invalidateQueries({ queryKey: ["counts", ws] });
      toast(
        s.error
          ? "Brandwall couldn't finish looking on Instagram. It tries again by itself."
          : s.added
            ? `Found ${s.added} new post${s.added === 1 ? "" : "s"}.`
            : "No new posts on Instagram right now.",
      );
    }
  }, [s, askedAt, qc, toast, ws]);

  const check = async () => {
    try {
      await post(`/v1/w/${ws}/instagram/sync`);
      setAskedAt(Date.now());
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };

  const busy = Boolean(askedAt) || s?.running;
  return (
    <div className="row wrap small">
      <span className="muted spacer">
        {busy
          ? "Looking on Instagram for new posts…"
          : s?.finishedAt
            ? `Last checked ${timeAgo(s.finishedAt)}. Brandwall checks by itself every 30 minutes.`
            : "Brandwall checks Instagram by itself every 30 minutes."}
      </span>
      {workspace.role !== "viewer" && (
        <button type="button" className="btn secondary small" disabled={busy} onClick={check}>
          {busy ? "Checking…" : "Check for new posts now"}
        </button>
      )}
    </div>
  );
}
